"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
  type ActivityPostFeedItem,
  type ActivityPostFeedResponse,
  getActivityPostsFeed,
} from "@/actions/activity-post";
import { profileTabKeys } from "@/lib/query-keys/profile-tabs";

// ──────────────────────────────────────────────────────────────────────────────
// QUERY: Activity Feed
// ──────────────────────────────────────────────────────────────────────────────

export function useActivityFeed(
  limit: number,
  initialPosts?: ActivityPostFeedItem[],
  options?: {
    userId?: string;
    enabled?: boolean;
    includePrivatePreview?: boolean;
  },
) {
  const queryKey = options?.userId
    ? profileTabKeys.userActivityPosts(
        options.userId,
        options.includePrivatePreview ?? false,
        limit,
      )
    : ([
        "activityPosts",
        "feed",
        limit,
        "home",
        options?.includePrivatePreview ? "with-preview" : "no-preview",
      ] as const);

  const initialData = initialPosts
    ? {
        pages: [
          {
            posts: initialPosts,
            hasMore: false,
            nextCursor: null,
          } as ActivityPostFeedResponse,
        ],
        pageParams: [null],
      }
    : undefined;

  return useInfiniteQuery({
    queryKey,
    enabled: options?.enabled,
    queryFn: async ({ pageParam }) => {
      const result = await getActivityPostsFeed({
        userId: options?.userId,
        includePrivatePreview: options?.includePrivatePreview,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch activity posts");
      }

      return result.data;
    },
    initialPageParam: null as number | null,
    initialData,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
}
